import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type Match } from '@/lib/site';
import LiveModal from '@/pages/detail/components/LiveModal';

interface ScoreboardProps {
  match: Match;
}

export default function Scoreboard({ match }: ScoreboardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const isLive = match.status === 'live';
  const isUpcoming = match.status === 'upcoming';
  const hasScore = !isUpcoming;

  const buttonLabel = isLive ? '观看直播' : isUpcoming ? '预约提醒' : '观看回放';
  const buttonIcon = isLive ? 'ri-play-circle-line' : isUpcoming ? 'ri-notification-3-line' : 'ri-history-line';

  return (
    <section aria-label="赛事比分与直播入口" className="relative w-full overflow-hidden rounded-lg bg-primary-950">
      <img
        src={match.cover}
        alt={`${match.home.name} 对阵 ${match.away.name} 赛事主视觉`}
        title={`${match.home.name} VS ${match.away.name} 直播`}
        className="absolute inset-0 w-full h-full object-cover object-top opacity-45"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-primary-950/95 via-primary-950/80 to-primary-900/70" />

      <div className="relative z-10 px-4 py-5 md:px-7 md:py-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                isLive
                  ? 'bg-accent-500 text-background-50'
                  : isUpcoming
                    ? 'bg-background-50/15 text-background-100'
                    : 'bg-background-50/10 text-background-300'
              }`}
            >
              {isLive && <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />}
              {isLive ? '直播中' : isUpcoming ? '即将开始' : '已结束'}
            </span>
            <span className="text-[11px] text-background-300">{match.leagueLabel}</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-background-300">
            <i className="ri-broadcast-line" />
            {match.broadcaster}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-3 items-center gap-2 md:gap-6">
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <span className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-50/10 border border-background-50/15 font-heading text-sm md:text-base text-background-50 flex items-center justify-center">
              {match.home.shortName}
            </span>
            <span className="mt-2.5 font-heading text-sm md:text-lg text-background-50 leading-snug">
              {match.home.name}
            </span>
            <span className="mt-1 text-[11px] text-background-400">{match.home.record}</span>
          </div>

          <div className="flex flex-col items-center">
            {hasScore ? (
              <div className="flex items-center gap-2 md:gap-4">
                <span className={`font-heading text-3xl md:text-5xl leading-none ${isLive ? 'text-accent-400' : 'text-background-50'}`}>
                  {match.home.score}
                </span>
                <span className="font-heading text-lg text-background-400">:</span>
                <span className={`font-heading text-3xl md:text-5xl leading-none ${isLive ? 'text-accent-400' : 'text-background-50'}`}>
                  {match.away.score}
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <span className="font-heading text-sm md:text-base text-accent-400">{match.timeLabel}</span>
                <span className="mt-1 text-[11px] text-background-400">尚未开赛</span>
              </div>
            )}
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-background-300">
              {isLive ? (
                <>
                  <i className="ri-time-line" />
                  {match.quarter}
                </>
              ) : (
                <>
                  <i className="ri-calendar-line" />
                  {isUpcoming ? '待开赛' : '比赛已结束'}
                </>
              )}
            </span>
          </div>

          <div className="flex flex-col items-center text-center md:items-end md:text-right">
            <span className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-background-50/10 border border-background-50/15 font-heading text-sm md:text-base text-background-50 flex items-center justify-center">
              {match.away.shortName}
            </span>
            <span className="mt-2.5 font-heading text-sm md:text-lg text-background-50 leading-snug">
              {match.away.name}
            </span>
            <span className="mt-1 text-[11px] text-background-400">{match.away.record}</span>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-[11px] text-background-300 mb-1.5">
            <span>{isUpcoming ? '赛前准备中' : `比赛进度 ${match.progress}%`}</span>
            <span>{match.clock}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-background-50/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-accent-400 to-accent-500 transition-all duration-700"
              style={{ width: `${Math.max(match.progress, isUpcoming ? 3 : 0)}%` }}
            />
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`${buttonIcon} text-base`} />
            {buttonLabel}
          </button>
          <span className="text-[11px] leading-relaxed text-background-400">
            当前为前端演示：尚未接入真实流媒体，点击按钮可查看说明
          </span>
        </div>
      </div>

      {modalOpen && <LiveModal match={match} onClose={() => setModalOpen(false)} />}

      {!isLive && (
        <div className="relative z-10 px-4 pb-4 md:px-7 md:pb-6">
          <Link
            to={`/category/${match.league}`}
            className="text-[11px] text-background-300 hover:text-accent-300 transition-colors inline-flex items-center gap-1"
          >
            <i className="ri-trophy-line" />
            前往 {match.leagueLabel} 分类查看更多赛事
          </Link>
        </div>
      )}
    </section>
  );
}